import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import ChildSiblingForm from 'UI/components/organisms/AssociatedCompanies/ChildSiblingForm';
import ParentForm from 'UI/components/organisms/AssociatedCompanies/ParentForm';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { COMPANY_RELATIONSHIP_KEY, COMPANY_RELATIONSHIP_TYPES } from 'UI/constants/defaults';

import { useStyles } from './styles';

/** @typedef {Object} Record */

/**
 * @typedef {Object} CurrentCompany
 * @property {string} name
 * @property {number} parentCompanyId
 */

const RELATIONSHIP_KEY_MAP = {
  PARENT: 'parentCompany',
  CHILD: 'childCompanies',
  SIBLING: 'siblingCompanies'
};

const getFormToRender = (relationship, companyId) => {
  const CONTENT_MAP = {
    [COMPANY_RELATIONSHIP_TYPES.PARENT]: <ParentForm companyId={companyId} />,
    [COMPANY_RELATIONSHIP_TYPES.SIBLING]: (
      <ChildSiblingForm
        linkType={COMPANY_RELATIONSHIP_KEY.siblingCompanies}
        companyId={companyId}
      />
    ),
    [COMPANY_RELATIONSHIP_TYPES.CHILD]: (
      <ChildSiblingForm linkType={COMPANY_RELATIONSHIP_KEY.childCompanies} companyId={companyId} />
    ),
    DEFAULT: null
  };

  return CONTENT_MAP[relationship] || CONTENT_MAP.DEFAULT;
};

const getDefaultCompanies = companies => (companies?.length ? companies : []);

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onSubmit
 * @param {number} [props.companyId]
 * @param {string} props.title
 * @param {string} props.associatedCompaniesRelationship
 * @param {CurrentCompany} props.currentCompany
 * @param {Record} props.associatedCompanies
 */
const AssociatedCompaniesDrawer = ({
  associatedCompanies,
  associatedCompaniesRelationship,
  currentCompany,
  onClose,
  onSubmit,
  companyId,
  open,
  title
}) => {
  const classes = useStyles();
  const { parentCompany, siblingCompanies, childCompanies } = associatedCompanies;

  const defaultValues = {
    parentCompany: parentCompany || { id: currentCompany?.parentCompanyId },
    siblingCompanies: getDefaultCompanies(siblingCompanies),
    childCompanies: getDefaultCompanies(childCompanies),
    currentCompany
  };

  const [isSaving, setIsSaving] = useState(false);

  const form = useForm({ defaultValues });
  const { handleSubmit, watch } = form;
  const formValues = watch();

  const content = getFormToRender(associatedCompaniesRelationship, companyId);

  const handleSave = async () => {
    if (companyId) {
      setIsSaving(true);
      const successfulRequest = await onSubmit(formValues, associatedCompaniesRelationship);
      setIsSaving(false);
      if (successfulRequest) onClose();
      return;
    }

    if (!formValues.parentCompany) formValues.siblingCompanies = null;
    onSubmit(formValues, associatedCompaniesRelationship);
    onClose();
  };

  const isAddingParent = associatedCompaniesRelationship === COMPANY_RELATIONSHIP_TYPES.PARENT;

  const drawerStyle = !isAddingParent ? classes.largeDrawer : null;

  const hasParentItemSelected =
    formValues[RELATIONSHIP_KEY_MAP[associatedCompaniesRelationship]]?.id;

  const hasChildSiblingItemsSelected =
    formValues[RELATIONSHIP_KEY_MAP[associatedCompaniesRelationship]]?.length;

  const isSaveDisabled = isAddingParent ? !hasParentItemSelected : !hasChildSiblingItemsSelected;

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: {
        md: 'auto'
      }
    },
    {
      props: {
        text: isSaving ? strings.shared.ui.saving : strings.shared.ui.save,
        variant: 'contained',
        disabled: (companyId && isSaveDisabled) || isSaving,
        onClick: handleSubmit(handleSave)
      },
      GridItemProps: {
        md: 'auto'
      },
      mode: 'default'
    }
  ];

  return (
    <FPDrawer
      FPDrawerProps={{ open, onClose, classes: { paper: drawerStyle } }}
      CardHeaderProps={{ title, id: 'drawer-header' }}
      ContentWrapper="div"
      ContentWrapperProps={{ onSubmit: handleSubmit(handleSave), id: 'drawer-form' }}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <FormContext {...form}>{content}</FormContext>
    </FPDrawer>
  );
};

AssociatedCompaniesDrawer.defaultProps = {
  open: false,
  title: null,
  associatedCompaniesRelationship: 'PARENT',
  currentCompany: null,
  onSubmit: null,
  companyId: null,
  associatedCompanies: {
    parentCompany: null,
    siblingCompanies: null,
    childCompanies: null
  }
};

export default AssociatedCompaniesDrawer;
